import { useTranslation } from 'react-i18next';
import { NoConsentData } from '../../../api/noConsentApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { formatCurrency, formatNumber, formatPercentage } from '../../../utils/formatters';

interface NoConsentTableProps {
  dailyMetrics: NoConsentData[];
  isLoading: boolean;
}

export function NoConsentTable({ dailyMetrics, isLoading }: NoConsentTableProps) {
  const { t } = useTranslation();

  // Calculer les métriques supplémentaires
  const enrichedMetrics = dailyMetrics.map(metric => {
    const totalViews = metric.paidPvs + metric.organicPvs;
    const totalClicks = metric.paidClicks + metric.organicClicks;
    const ctr = totalViews > 0 ? (totalClicks / totalViews) * 100 : 0;
    const revenuePerView = totalViews > 0 ? (metric.revenue / totalViews) * 1000 : 0;

    return {
      ...metric,
      totalViews,
      totalClicks,
      ctr,
      revenuePerView
    };
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('noconsent.table.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('noconsent.table.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('noconsent.table.date')}</TableHead>
              <TableHead className="text-right">{t('noconsent.table.totalViews')}</TableHead>
              <TableHead className="text-right">{t('noconsent.table.paidViews')}</TableHead>
              <TableHead className="text-right">{t('noconsent.table.organicViews')}</TableHead>
              <TableHead className="text-right">{t('noconsent.table.totalClicks')}</TableHead>
              <TableHead className="text-right">{t('noconsent.table.ctr')}</TableHead>
              <TableHead className="text-right">{t('noconsent.table.viewability')}</TableHead>
              <TableHead className="text-right">{t('noconsent.table.grossRevenue')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enrichedMetrics.map((metric, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">
                  {new Date(metric.date).toLocaleDateString('fr-FR', { 
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </TableCell>
                <TableCell className="text-right">{formatNumber(metric.totalViews)}</TableCell>
                <TableCell className="text-right">{formatNumber(metric.paidPvs)}</TableCell>
                <TableCell className="text-right">{formatNumber(metric.organicPvs)}</TableCell>
                <TableCell className="text-right">{formatNumber(metric.totalClicks)}</TableCell>
                <TableCell className="text-right">{formatPercentage(metric.ctr)}</TableCell>
                <TableCell className="text-right">{formatPercentage(metric.viewabilityRate)}</TableCell>
                <TableCell className="text-right">{formatCurrency(metric.revenue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}